<template>
  <div class="wrraper activity_logs_page">
    <!-- Start Breadcrumb -->
    <Breadcrumb :items="items" />
    <!-- End Breadcrumb -->

    <!-- Start 403 State -->
    <div v-if="forbidden" class="forbidden_state text-center">
      <i class="fal fa-lock mb-3"></i>
      <p>{{ forbiddenMessage || $t("activityLogs.forbidden") }}</p>
    </div>
    <!-- End 403 State -->

    <template v-else>
      <div class="card-header mb-4">
        <h4 class="card-title">
          <i class="fas fa-history ml-2"></i>
          {{ $t("activityLogs.title") }}
        </h4>
      </div>

      <!-- Start Filters -->
      <div class="d-flex align-items-center filters-row">
        <the-filter class="flex-grow-1" :inputs="inputs" />
        <button
          type="button"
          class="reset-btn flex-shrink-0"
          :title="$t('activityLogs.filters.reset')"
          @click="resetFilters"
        >
          <i class="fal fa-undo"></i>
        </button>
      </div>
      <!-- End Filters -->

      <!-- Start Error State (filters stay so the user can retry) -->
      <div v-if="errorMessage" class="error_state text-center mb-4">
        <p class="text-danger mb-3">{{ errorMessage }}</p>
        <v-btn color="primary" outlined :disabled="loading" @click="loadLogs">
          {{ $t("activityLogs.retry") }}
        </v-btn>
      </div>
      <!-- End Error State -->

      <!-- Start Loading Skeleton -->
      <v-skeleton-loader
        v-if="loading && !rows.length"
        type="table-thead, table-tbody"
      ></v-skeleton-loader>
      <!-- End Loading Skeleton -->

      <!-- Start Table -->
      <main v-else class="magic-table-wrapper">
        <v-data-table
          class="thumb strip"
          :headers="headers"
          :items="rows"
          :loading="loading"
          :loading-text="$t('table.loadingData')"
          item-key="id"
          :items-per-page="-1"
          :expanded.sync="expanded"
          show-expand
          single-expand
          disable-sort
          hide-default-footer
        >
          <!-- Actor -->
          <template v-slot:[`item.actor`]="{ item }">
            <div v-if="item.user" class="actor_cell">
              <img
                v-if="item.user.avatar"
                class="actor_avatar"
                :src="item.user.avatar"
                alt=""
              />
              <div class="actor_info">
                <div class="actor_name">{{ item.user.full_name }}</div>
                <small class="text-muted">
                  {{ userTypeLabel(item.user.user_type) }}
                </small>
                <small v-if="item.user.phone" class="d-block" dir="ltr">
                  {{ (item.user.phone_code || "") + item.user.phone }}
                </small>
              </div>
            </div>
            <span v-else class="text-muted">-</span>
          </template>

          <template v-slot:[`item.description`]="{ item }">
            <div class="log_text">{{ dash(item.description) }}</div>
          </template>

          <template v-slot:[`item.event`]="{ item }">
            <span :class="['event_label', item.event]">
              {{ dash(item.event) }}
            </span>
          </template>

          <template v-slot:[`item.module`]="{ item }">
            {{ dash(item.module) }}
          </template>

          <!-- Affected record -->
          <template v-slot:[`item.subject`]="{ item }">
            <div>{{ subjectLabel(item) }}</div>
            <small class="text-muted" dir="ltr">
              {{ dash(item.subject_type) }}
              <template v-if="hasValue(item.subject_id)">
                #{{ item.subject_id }}
              </template>
            </small>
          </template>

          <!-- Old / New values -->
          <template v-slot:[`item.old`]="{ item }">
            <log-values :value="item.old" :empty-text="$t('activityLogs.noValue')" />
          </template>

          <template v-slot:[`item.new`]="{ item }">
            <log-values :value="item.new" :empty-text="$t('activityLogs.noValue')" />
          </template>

          <template v-slot:[`item.ip_address`]="{ item }">
            <span dir="ltr">{{ dash(item.ip_address) }}</span>
            <small v-if="item.ip_location" class="d-block text-muted">
              {{ formatScalar(item.ip_location) }}
            </small>
          </template>

          <template v-slot:[`item.created_at`]="{ item }">
            <span dir="ltr">{{ dash(item.created_at) }}</span>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <div class="_actions">
              <v-icon class="delete" small @click="askDelete(item)">
                fal fa-trash
              </v-icon>
            </div>
          </template>

          <!-- Expanded row: the full record, so no response key is lost -->
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="expanded_cell">
              <strong>{{ $t("activityLogs.details") }}</strong>
              <pre dir="ltr">{{ JSON.stringify(item, null, 2) }}</pre>
            </td>
          </template>

          <!-- Empty state -->
          <template v-slot:no-data>
            <span v-if="!errorMessage">{{ $t("activityLogs.empty") }}</span>
          </template>
        </v-data-table>
      </main>
      <!-- End Table -->

      <!-- Start Cursor Pagination -->
      <div
        class="pagination_container text-center my-4 d-flex justify-content-end align-items-center"
      >
        <div class="select-pagination d-flex">
          <span class="first">{{ $t("show") }}</span>
          <v-select
            :items="chunkOptions"
            v-model="chunk"
            @change="applyFilters"
          ></v-select>
          <span>{{ $t("entries") }}</span>
        </div>
        <v-btn
          class="mx-1 prev-btn"
          color="primary"
          outlined
          :disabled="loading || !meta.has_previous"
          @click="goPrevious"
        >
          <v-icon small class="mx-1">
            {{ isRtl ? "fal fa-angle-right" : "fal fa-angle-left" }}
          </v-icon>
          {{ $t("activityLogs.previous") }}
        </v-btn>
        <v-btn
          class="mx-1 next-btn"
          color="primary"
          depressed
          :disabled="loading || !meta.has_more"
          @click="goNext"
        >
          {{ $t("activityLogs.next") }}
          <v-icon small class="mx-1">
            {{ isRtl ? "fal fa-angle-left" : "fal fa-angle-right" }}
          </v-icon>
        </v-btn>
      </div>
      <!-- End Cursor Pagination -->

      <!-- Start Delete Dialog -->
      <v-dialog v-model="dialogDelete" max-width="500px">
        <v-card>
          <v-card-title class="text-h5 justify-center">
            {{ $t("table.deletedialog.areYouSure") }}
          </v-card-title>
          <v-card-text class="text-center" v-if="itemToDelete">
            {{ $t("activityLogs.deleteConfirm") }} (#{{ itemToDelete.id }})
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="#1B5E20"
              :loading="deleting"
              :disabled="deleting"
              @click="confirmDelete"
            >
              {{ $t("table.deletedialog.ok") }}
            </v-btn>
            <v-btn color="#F44336" :disabled="deleting" @click="closeDelete">
              {{ $t("table.deletedialog.cancel") }}
            </v-btn>
            <v-spacer></v-spacer>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <!-- End Delete Dialog -->
    </template>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import TheFilter from "@/components/Filters/TheFilter.vue";

const DEFAULT_CHUNK = 15;

// Module keys the backend logs (one per logged model)
const MODULES = [
  "user",
  "driver",
  "store",
  "client",
  "admin",
  "company",
  "branch",
  "country",
  "place_category",
  "place",
  "slider",
  "plan",
  "referral_reward",
  "coupon",
  "offer",
  "menu",
  "meal",
  "order",
  "driver_fine",
  "cash_out_request",
  "bonus",
];

const EVENTS = ["created", "updated", "deleted"];

// Renders an old / new payload: null, scalars, objects and nested arrays
// (work-time schedules) without losing any key.
const LogValues = {
  name: "LogValues",
  functional: true,
  props: { value: { default: null }, emptyText: { type: String, default: "-" } },
  render(h, { props }) {
    const { value, emptyText } = props;
    const isEmpty =
      value === null ||
      value === undefined ||
      (typeof value === "object" && Object.keys(value).length === 0);

    if (isEmpty) return h("span", { class: "text-danger" }, emptyText);

    const scalar = (v) =>
      h("span", { class: "log_scalar" }, v === null || v === undefined ? "-" : String(v));

    // Nested objects / arrays become indented key rows instead of raw JSON
    const node = (v) => {
      if (v === null || typeof v !== "object") return scalar(v);

      // A time period { from, to } reads as one line
      if (!Array.isArray(v) && "from" in v && "to" in v && Object.keys(v).length === 2) {
        return h("span", { class: "log_scalar", attrs: { dir: "ltr" } }, `${v.from} → ${v.to}`);
      }

      const entries = Array.isArray(v)
        ? v.map((item, i) => [i + 1, item])
        : Object.entries(v);

      return h(
        "div",
        { class: "log_nested" },
        entries.map(([key, item]) =>
          h("div", { key, class: "log_value_row" }, [
            h("span", { class: "log_key" }, String(key)),
            node(item),
          ])
        )
      );
    };

    return h("div", { class: "log_values" }, [node(value)]);
  },
};

export default {
  name: "ActivityLogs",

  components: { LogValues, TheFilter },

  data() {
    return {
      // ========== Breadcrumbs
      items: [
        {
          text: this.$t("breadcrumb.mainPage"),
          disabled: false,
          href: "/",
        },
        {
          text: this.$t("breadcrumb.logs.title"),
          disabled: false,
          href: "/logs/show-all",
        },
        {
          text: this.$t("breadcrumb.logs.all"),
          disabled: true,
          href: "",
        },
      ],

      // ========== Filters (same the-filter component, driven by the route query)
      inputs: [
        { keyName: "start_date", col: 3 },
        { keyName: "end_date", col: 3 },
        {
          keyName: "event",
          select: true,
          col: 3,
          label: this.$t("activityLogs.filters.event"),
          options: EVENTS.map((id) => ({
            id,
            name: this.translateOr(`activityLogs.events.${id}`, id),
          })),
        },
        {
          keyName: "module",
          select: true,
          col: 3,
          label: this.$t("activityLogs.filters.module"),
          options: MODULES.map((id) => ({
            id,
            name: this.translateOr(`activityLogs.modules.${id}`, id),
          })),
        },
        {
          keyName: "causer_id",
          text: true,
          col: 3,
          label: this.$t("activityLogs.filters.causerId"),
        },
        {
          keyName: "causer_type",
          text: true,
          col: 3,
          label: this.$t("activityLogs.filters.causerType"),
        },
        {
          keyName: "subject_id",
          text: true,
          col: 3,
          label: this.$t("activityLogs.filters.subjectId"),
        },
        {
          keyName: "subject_type",
          text: true,
          col: 3,
          label: this.$t("activityLogs.filters.subjectType"),
        },
      ],
      chunk: DEFAULT_CHUNK,
      chunkOptions: [15, 30, 50, 100],

      // ========== Data
      rows: [],
      expanded: [],
      meta: { chunk: DEFAULT_CHUNK, last_id: null, previous_id: null, has_more: false, has_previous: false },
      // Cursor sent with the next request: { last_id } or { previous_id }
      cursor: {},

      // ========== States
      loading: false,
      forbidden: false,
      forbiddenMessage: "",
      errorMessage: "",
      // Ignore a slow response when a newer request was already sent
      requestId: 0,

      // ========== Delete
      dialogDelete: false,
      itemToDelete: null,
      deleting: false,
    };
  },

  computed: {
    ...mapGetters({ lang: "lang_module/lang" }),

    isRtl() {
      return this.lang === "ar";
    },

    headers() {
      const col = (key) => this.$t(`activityLogs.columns.${key}`);
      return [
        { text: col("id"), value: "id", align: "center" },
        { text: col("actor"), value: "actor", align: "center" },
        { text: col("description"), value: "description", align: "center" },
        { text: col("event"), value: "event", align: "center" },
        { text: col("module"), value: "module", align: "center" },
        { text: col("subject"), value: "subject", align: "center" },
        { text: col("old"), value: "old", align: "center" },
        { text: col("new"), value: "new", align: "center" },
        { text: col("ip"), value: "ip_address", align: "center" },
        { text: col("date"), value: "created_at", align: "center" },
        { text: col("actions"), value: "actions", align: "center" },
        { text: "", value: "data-table-expand", sortable: false },
      ];
    },
  },

  watch: {
    // Every filter change made in the-filter lands in the route query
    "$route.query"() {
      this.applyFilters();
    },
  },

  methods: {
    // ========== Display helpers (raw values are never mutated)
    hasValue(value) {
      return value !== null && value !== undefined && value !== "";
    },

    dash(value) {
      return this.hasValue(value) ? value : "-";
    },

    formatScalar(value) {
      return value !== null && typeof value === "object"
        ? JSON.stringify(value)
        : String(value);
    },

    translateOr(key, fallback) {
      return this.$te(key) ? this.$t(key) : fallback;
    },

    userTypeLabel(type) {
      if (!this.hasValue(type)) return "-";
      return this.translateOr(
        `activityLogs.userTypes.${String(type).toLowerCase()}`,
        type
      );
    },

    isReturnAction(row) {
      return (
        /return/i.test(row.description || "") ||
        /return/i.test(row.module_key || "")
      );
    },

    // Delivery / package / return orders first, then the user types
    subjectLabel(row) {
      const key = String(row.subject_item_key || "").toLowerCase();
      if (this.isReturnAction(row)) return this.$t("activityLogs.subjectItems.return");
      if (key === "delivery" || key === "package") {
        return this.$t(`activityLogs.subjectItems.${key}`);
      }
      return this.userTypeLabel(row.subject_item_key);
    },

    // ========== Request
    buildParams() {
      const q = this.$route.query;
      const map = {
        start_date: "from_date",
        end_date: "to_date",
        event: "event",
        module: "module",
        causer_id: "causer_id",
        causer_type: "causer_type",
        subject_id: "subject_id",
        subject_type: "subject_type",
      };
      const params = { chunk: this.chunk };
      Object.entries(map).forEach(([queryKey, apiKey]) => {
        if (q[queryKey]) params[apiKey] = q[queryKey];
      });
      return { ...params, ...this.cursor };
    },

    // Filters changed: back to the newest page, both cursors cleared
    applyFilters() {
      this.cursor = {};
      this.loadLogs();
    },

    resetFilters() {
      this.chunk = DEFAULT_CHUNK;
      if (Object.keys(this.$route.query).length) {
        // The route watcher reloads the list
        this.$router.push({ query: {} }).catch(() => {});
      } else {
        this.applyFilters();
      }
    },

    goNext() {
      if (!this.meta.has_more) return;
      this.cursor = { last_id: this.meta.last_id };
      this.loadLogs();
    },

    goPrevious() {
      if (!this.meta.has_previous) return;
      this.cursor = { previous_id: this.meta.previous_id };
      this.loadLogs();
    },

    loadLogs() {
      const currentRequest = ++this.requestId;
      this.loading = true;
      this.errorMessage = "";

      this.$axios({
        method: "GET",
        url: "logs",
        params: this.buildParams(),
      })
        .then((res) => {
          if (currentRequest !== this.requestId) return;
          const body = res.data || {};
          this.rows = Array.isArray(body.data) ? body.data : [];
          this.meta = {
            chunk: DEFAULT_CHUNK,
            last_id: null,
            previous_id: null,
            has_more: false,
            has_previous: false,
            ...(body.meta || {}),
          };
          this.expanded = [];
          this.loading = false;
        })
        .catch((err) => {
          if (currentRequest !== this.requestId) return;
          this.loading = false;
          const status = err.response?.status;
          const message = err.response?.data?.message;

          // 403: hide the page (the global interceptor also redirects)
          if (status === 403) {
            this.forbidden = true;
            this.forbiddenMessage = message || "";
            return;
          }
          // 401 is handled by the global axios interceptor (logout)
          if (status === 401) return;

          // 422 and any other error: show the backend message, keep filters
          this.errorMessage = message || this.$t("globalErr");
          if (status === 422) {
            this.$iziToast.error({
              timeout: 3000,
              message: this.errorMessage,
              position: "bottomRight",
            });
          }
        });
    },

    // ========== Delete
    askDelete(item) {
      this.itemToDelete = item;
      this.dialogDelete = true;
    },

    closeDelete() {
      this.dialogDelete = false;
      this.itemToDelete = null;
    },

    confirmDelete() {
      if (!this.itemToDelete) return;
      const { id } = this.itemToDelete;
      this.deleting = true;

      this.$axios({ method: "DELETE", url: `logs/${id}` })
        .then(() => {
          this.rows = this.rows.filter((row) => row.id !== id);
          this.closeDelete();
          this.$iziToast.success({
            timeout: 2000,
            message: this.$t("deleteSuccess"),
            position: "bottomRight",
          });
          // The page ran empty: refresh the same page for the next rows
          if (!this.rows.length) this.loadLogs();
        })
        .catch((err) => {
          this.closeDelete();
          this.$iziToast.error({
            timeout: 3000,
            message: err.response?.data?.message || this.$t("globalErr"),
            position: "bottomRight",
          });
        })
        .finally(() => {
          this.deleting = false;
        });
    },
  },

  created() {
    this.loadLogs();
  },
};
</script>

<style lang="scss" scoped>
.activity_logs_page {
  .card-header {
    background: transparent;
    border: none;
    padding: 0;

    .card-title {
      font-weight: bold;
      margin-bottom: 4px;
    }
  }

  .filters-row {
    column-gap: 10px;

    .reset-btn {
      width: 46px;
      height: 46px;
      flex: 0 0 46px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      border: 1px solid var(--mainColor-2);
      border-radius: 50%;
      background-color: transparent;
      color: var(--mainColor-2);
      cursor: pointer;
      transition: all 0.25s ease;

      &:hover,
      &:focus {
        background-color: var(--mainColor-2);
        color: #fff;
        outline: none;
      }
    }
  }

  .forbidden_state {
    padding: 60px 0;

    i {
      font-size: 5rem;
      color: #8a8a8a;
    }
  }

  .actor_cell {
    display: flex;
    align-items: center;
    justify-content: center;
    column-gap: 8px;
    text-align: start;

    .actor_avatar {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      object-fit: cover;
    }
  }

  .log_text {
    max-width: 240px;
    margin: 0 auto;
    white-space: pre-wrap;
    word-break: break-word;
  }

  .event_label {
    display: inline-block;
    padding: 2px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: bold;
    background: rgba(120, 61, 115, 0.12);

    &.created {
      color: #1b5e20;
      background: rgba(76, 175, 80, 0.15);
    }

    &.deleted {
      color: #b71c1c;
      background: rgba(244, 67, 54, 0.15);
    }
  }

  .expanded_cell {
    padding: 12px 16px;
    text-align: start;

    pre {
      margin: 8px 0 0;
      max-height: 320px;
      overflow: auto;
    }
  }
}
</style>

<style lang="scss">
// Rendered by the functional LogValues component, so not scoped
.log_values {
  max-width: 260px;
  margin: 0 auto;
  text-align: start;
  word-break: break-word;

  .log_value_row {
    margin-bottom: 2px;
  }

  // Keep the colon next to the key in both RTL and LTR
  .log_key {
    font-weight: bold;
    unicode-bidi: isolate;

    &::after {
      content: ":";
      margin-inline: 0 4px;
    }
  }

  .log_nested {
    padding-inline-start: 12px;
    margin-top: 2px;

    .log_nested {
      border-inline-start: 1px solid #e0e0e0;
    }
  }
}
</style>
